/**
 * Клієнт власного чату ефіру (див. backend/src/chat).
 *
 * Одне вебсокет-зʼєднання на вкладку: стрічка, надсилання, модерація й
 * лічильник онлайну — усе одним каналом. Історію не тягнемо окремим
 * запитом: сервер надсилає її першим же кадром після входу в кімнату, тож
 * стрічка приїжджає разом із підключенням, а не двома різними шляхами.
 *
 * Токен їде кадром, а не в адресі: адреси лишаються в логах nginx, і JWT
 * глядача разом із ними.
 *
 * Своє повідомлення показуємо одразу, не чекаючи на сервер: рядок стає в
 * стрічку блідим, а коли приїжджає справжній кадр — підміняється ним на
 * тому ж місці (зшиваємо за nonce, який самі ж і згенерували). Так чат
 * відповідає на Enter миттєво, а порядок стрічки лишається серверним.
 */

// Скільки повідомлень тримаємо в памʼяті. Ефір іде годинами, і без стелі
// вкладка до ночі з'їдає пам'ять на тексті, який ніхто вже не гортає
const MAX_MESSAGES = 300

// Перепідключення: перша спроба майже одразу (частіше за все це просто
// заснулий вайфай), далі рідше — щоб не довбати бекенд, який лежить
const RECONNECT_MIN = 1000
const RECONNECT_MAX = 20000

// Як часто перепитуємо доступ, коли чат нас не пустив. Підписку
// оформляють просто під час ефіру, і людині не має бути потрібно
// перезавантажувати сторінку, щоб чат нарешті відкрився
const RETRY_JOIN = 45000

// Скільки чекаємо на підтвердження свого ж повідомлення. Довше — і бліде
// «надсилається» висить у стрічці назавжди, хоча звʼязку вже немає
const ACK_TIMEOUT = 10000

export const useLiveChat = (room, options = {}) => {
  const config = useRuntimeConfig()
  const token = useStrapiToken()

  const messages = ref([])
  // «Підключені» означає не «сокет відкрився», а «ми вже в кімнаті»: між
  // цими двома подіями бекенд ще перевіряє токен і доступ, і надіслане в
  // цю щілину повідомлення йому нікуди покласти
  const connected = ref(false)
  // Профіль глядача очима чату: імʼя, права модератора, мовчанка. null —
  // не залогінений, тобто може читати, але не писати
  const me = ref(null)
  const online = ref(0)
  const muted = ref(false)
  const mutedUntil = ref(null)
  // Чат закритий підпискою — те саме правило, що й на сам ефір. Усередині
  // лежить причина: чого саме бракує (див. екран у ChatLiveBody)
  const denied = ref(null)
  // Джерело для OBS заходить ключем: читати можна, писати нема кому.
  // Незалогіненого глядача це не стосується — йому чат показує кнопки
  // входу, а не ховає поле
  const readonly = ref(false)
  // Останнє «не вийшло»: занадто швидко, замучено, завелике медіа
  const notice = ref('')
  const limits = ref({ maxText: 500, maxMediaBytes: 10 * 1024 * 1024 })
  // Хто зараз у кімнаті — для підказок «@». Сервер віддає список на вході,
  // далі доповнюємо його авторами зі стрічки: підказувати варто рівно тих,
  // кого в чаті видно
  const chatters = ref([])
  // Не прийняте сервером повідомлення: композер поверне текст у поле, щоб
  // набране не зникало в нікуди
  const rejected = ref(null)
  // Опитування, що зараз висить над стрічкою (запускає модератор). null —
  // немає жодного. Живе на сервері в памʼяті процесу, тож приїжджає разом
  // із входом у кімнату — так само, як історія
  const poll = ref(null)

  const canModerate = computed(() => !!me.value?.moderator)

  let socket = null
  let retry = RECONNECT_MIN
  let retryTimer = null
  let joinTimer = null
  let closedByUs = false

  const apiBase = computed(() => config.public.apiBase || '')

  const wsUrl = computed(() => {
    const base = apiBase.value
    if (!base) return ''
    // Той самий хост, що й у решти API, лише схема інша. У проді це
    // https → wss, локально http → ws
    return `${base.replace(/^http/, 'ws').replace(/\/+$/, '')}/ws/chat`
  })

  const push = (message) => {
    const list = messages.value
    // Кадр може приїхати двічі (перепідключення накладається на історію) —
    // краще перевірити, ніж показати те саме двома рядками
    if (list.some((m) => m.id === message.id)) return
    messages.value = [...list.slice(-(MAX_MESSAGES - 1)), message]
    rememberChatter(message.author)
  }

  const replace = (message) => {
    messages.value = messages.value.map((m) => {
      if (m.id === message.id) return message
      // Видалили те, на що вже встигли відповісти: цитата має згаснути
      // разом з оригіналом, інакше прибране модератором лишалось би в
      // чаті стільки разів, скільки на нього відповіли
      if (message.deleted && m.replyTo?.id === message.id) {
        return { ...m, replyTo: { ...m.replyTo, text: '', media: false, deleted: true } }
      }
      return m
    })
  }

  const rememberChatter = (name) => {
    if (!name || chatters.value.includes(name)) return
    chatters.value = [...chatters.value, name]
  }

  // --- Своє повідомлення до відповіді сервера ---
  //
  // nonce -> таймер: не дочекались підтвердження — прибираємо бліде з
  // стрічки й повертаємо текст у поле
  const pending = new Map()

  const newNonce = () =>
    // crypto є в кожному браузері, де є вебсокети; запасний варіант — на
    // випадок http-контексту в розробці, де crypto.randomUUID немає
    globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`

  const dropPending = (nonce, { reject = null } = {}) => {
    const timer = pending.get(nonce)
    if (timer) clearTimeout(timer)
    pending.delete(nonce)

    const failed = messages.value.find((m) => m.nonce === nonce)
    messages.value = messages.value.filter((m) => m.nonce !== nonce)

    if (reject && failed) rejected.value = { ...failed, reason: reject }
  }

  /**
   * Підміняє бліде повідомлення справжнім — на тому ж місці, а не в кінці
   * стрічки: між надсиланням і відповіддю сервера могли встигнути
   * написати інші
   */
  const settle = (nonce, message) => {
    const timer = pending.get(nonce)
    if (timer) clearTimeout(timer)
    pending.delete(nonce)

    let found = false
    const next = messages.value.map((m) => {
      if (m.nonce !== nonce) return m
      found = true
      return message
    })

    if (!found) return false
    messages.value = next
    return true
  }

  const HANDLERS = {
    hello: (data) => {
      connected.value = true
      denied.value = null
      readonly.value = !!data.readonly
      me.value = data.me || null
      online.value = data.online || 0
      muted.value = !!data.muted
      mutedUntil.value = data.me?.mutedUntil || null
      if (data.limits) limits.value = data.limits
      chatters.value = data.chatters || []
    },
    history: (data) => {
      messages.value = (data.messages || []).slice(-MAX_MESSAGES)
      for (const message of messages.value) rememberChatter(message.author)
    },
    // Кадр із nonce — це підтвердження чийогось надсилання. Своє бліде
    // підміняємо ним на місці; чуже (як і своє з іншої вкладки) просто
    // додаємо в кінець
    message: (data) => {
      if (data.nonce && settle(data.nonce, data.message)) {
        rememberChatter(data.message.author)
        return
      }
      push(data.message)
    },
    update: (data) => replace(data.message),
    online: (data) => {
      online.value = data.count || 0
    },
    muted: (data) => {
      muted.value = !!data.until
      mutedUntil.value = data.until || null
      if (me.value) me.value = { ...me.value, mutedUntil: data.until || null }
    },
    // Профіль змінився з наших же рук: нік, колір чи значок. Кадр приходить
    // у відповідь на запит клієнта, а не сам по собі
    me: (data) => {
      me.value = data.me || me.value
      mutedUntil.value = data.me?.mutedUntil || null
    },
    // Опитування. Кадр із розсилки не знає, за що проголосували саме ми
    // (сервер не роздає кімнаті чужі голоси) — тож свій голос зберігаємо
    // з попереднього стану
    poll: (data) => {
      const next = data.poll
      if (!next) return
      const mine =
        next.myVote ?? (poll.value?.id === next.id ? poll.value.myVote : null)
      poll.value = { ...next, myVote: mine }
    },
    pollClear: () => {
      poll.value = null
    },
    // Системні рядки («такого-то замучено») живуть у тій самій стрічці:
    // окремої «консолі модератора» в чаті немає, та й не треба
    system: (data) => {
      push({
        id: `system-${Date.now()}-${Math.random()}`,
        system: true,
        text: data.text,
        at: new Date().toISOString()
      })
    },
    // Не пустили: кімнати нам не дали взагалі — ні історії, ні нових
    // повідомлень. Показуємо екран входу й час від часу пробуємо ще раз
    denied: (data) => {
      denied.value = data
      messages.value = []
      me.value = null
      online.value = 0
      chatters.value = []
      poll.value = null
      forgetPending()
      scheduleJoin()
    },
    error: (data) => {
      notice.value = data.text || 'Щось пішло не так'
      // Помилка про конкретне надсилання: прибираємо бліде й повертаємо
      // набране в поле — інакше воно зникло б разом із рядком
      if (data.nonce) dropPending(data.nonce, { reject: notice.value })
    }
  }

  // Перепідключення й закриті кімнати: усе, чого ми так і не дочекались,
  // лишається в стрічці блідим назавжди, якщо про нього не прибрати.
  // З причиною — набране повернеться в поле, без неї просто зникне
  const forgetPending = (reason = null) => {
    for (const nonce of [...pending.keys()]) dropPending(nonce, { reject: reason })
  }

  const send = (payload) => {
    if (socket?.readyState !== WebSocket.OPEN) return false
    socket.send(JSON.stringify(payload))
    return true
  }

  const join = () => {
    if (joinTimer) {
      clearTimeout(joinTimer)
      joinTimer = null
    }
    send({
      type: 'join',
      room: toValue(room),
      token: token.value || null,
      // Ключ джерела для OBS — тільки на сторінці оверлея (див.
      // pages/live-chat.vue); у звичайного глядача його немає
      key: toValue(options.overlayKey) || null
    })
  }

  const scheduleJoin = () => {
    if (joinTimer || closedByUs) return
    joinTimer = setTimeout(() => {
      joinTimer = null
      join()
    }, RETRY_JOIN)
  }

  const scheduleReconnect = () => {
    if (closedByUs || retryTimer) return
    retryTimer = setTimeout(() => {
      retryTimer = null
      retry = Math.min(retry * 2, RECONNECT_MAX)
      connect()
    }, retry)
  }

  const connect = () => {
    if (!import.meta.client || closedByUs) return
    if (!wsUrl.value || !toValue(room)) return
    if (socket && socket.readyState <= WebSocket.OPEN) return

    try {
      socket = new WebSocket(wsUrl.value)
    } catch (e) {
      scheduleReconnect()
      return
    }

    socket.onopen = () => {
      // Не connected: у кімнату нас ще не пустили — це скаже hello
      retry = RECONNECT_MIN
      join()
    }

    socket.onmessage = (event) => {
      let data
      try {
        data = JSON.parse(event.data)
      } catch (e) {
        return
      }
      HANDLERS[data?.type]?.(data)
    }

    socket.onclose = () => {
      connected.value = false
      socket = null
      // Що не встигли підтвердити до розриву — не підтвердиться вже
      // ніколи: історія приїде наново з сервера, а набране повернеться
      // в поле
      forgetPending('Немає звʼязку з чатом — спробуйте ще раз')
      scheduleReconnect()
    }

    // Помилку не розбираємо окремо: за нею завжди йде close, там і
    // перепідключаємось
    socket.onerror = () => {}
  }

  const disconnect = () => {
    closedByUs = true
    forgetPending()
    if (retryTimer) clearTimeout(retryTimer)
    retryTimer = null
    if (joinTimer) clearTimeout(joinTimer)
    joinTimer = null
    if (socket) {
      socket.onclose = null
      socket.close()
      socket = null
    }
    connected.value = false
  }

  /**
   * Надіслати повідомлення. Медіа вже має бути завантажене (uploadMedia) —
   * у сокет їдуть тільки номери файлів (до пʼяти).
   *
   * Рядок стає в стрічку одразу, ще до відповіді сервера: чат має
   * відповідати на Enter, а не на мережу. Справжній кадр підмінить його на
   * тому ж місці (settle), а якщо сервер не прийняв — рядок зникне, і
   * набране повернеться в поле (rejected)
   *
   * @param {object} payload
   * @param {number[]} [payload.mediaIds] номери завантажених файлів
   * @param {object[]} [payload.media] завантажені вкладення — тільки щоб
   *   показати їх у блідому рядку до відповіді сервера
   * @param {object} [payload.replyTo] повідомлення, на яке відповідаємо
   */
  const sendMessage = ({ text, mediaIds = [], spoiler = false, media = [], replyTo = null }) => {
    notice.value = ''
    rejected.value = null

    const body = String(text || '').slice(0, limits.value.maxText)
    const nonce = newNonce()

    const sent = send({
      type: 'message',
      text: body,
      mediaIds,
      spoiler: !!spoiler,
      replyTo: replyTo?.id || null,
      nonce
    })

    if (!sent) return false

    // Показуємо своє ще до відповіді. id тимчасовий і в стрічці ні на що
    // не впливає: видалення й модерація доступні тільки на підтвердженому
    // рядку (див. ChatLiveMessage)
    messages.value = [
      ...messages.value.slice(-(MAX_MESSAGES - 1)),
      {
        id: `pending-${nonce}`,
        nonce,
        pending: true,
        room: toValue(room),
        author: me.value?.name || '',
        userId: me.value?.id || null,
        moderator: !!me.value?.moderator,
        text: body,
        media: media || [],
        spoiler: !!spoiler,
        deleted: false,
        deletedBy: null,
        replyTo: replyTo
          ? {
              id: replyTo.id,
              author: replyTo.author,
              text: replyTo.text || '',
              media: !!replyTo.media,
              deleted: !!replyTo.deleted
            }
          : null,
        at: new Date().toISOString()
      }
    ]

    pending.set(
      nonce,
      setTimeout(() => {
        dropPending(nonce, { reject: 'Немає звʼязку з чатом — спробуйте ще раз' })
        notice.value = 'Немає звʼязку з чатом — спробуйте ще раз'
      }, ACK_TIMEOUT)
    )

    return true
  }

  const removeMessage = (id) => send({ type: 'delete', id })
  const muteUser = (userId, seconds) => send({ type: 'mute', userId, seconds })

  /**
   * Реакція на повідомлення (емодзі або «:назва» смайла): повторна реакція
   * тим самим знімає свою. Оновлене повідомлення приїде кадром update
   */
  const reactMessage = (id, emoji) => {
    if (!me.value) {
      notice.value = 'Увійдіть, щоб реагувати'
      return false
    }
    if (typeof id !== 'number') return false
    return send({ type: 'react', id, emoji })
  }

  /**
   * Перечитати свій профіль після зміни налаштувань (нік, колір, значок).
   *
   * Зʼєднання оновлює профіль саме, але не раніше ніж за 15 секунд — а
   * перше ж повідомлення після зміни має піти вже новим іменем
   */
  const refreshProfile = () => send({ type: 'profile' })

  // --- Опитування ---
  const createPoll = ({ question, options, seconds }) =>
    send({ type: 'poll', question, options, seconds })

  const votePoll = (option) => {
    if (!poll.value || poll.value.closed) return false
    // Показуємо свій вибір одразу: сервер підтвердить його наступним же
    // кадром, а кнопка не має чекати на мережу
    poll.value = { ...poll.value, myVote: option }
    return send({ type: 'vote', pollId: poll.value.id, option })
  }

  const endPoll = () => poll.value && send({ type: 'pollEnd', pollId: poll.value.id })

  const dropPoll = () => {
    if (!poll.value) return false
    const sent = send({ type: 'pollDrop', pollId: poll.value.id })
    // Модератор прибрав картку — у нього вона зникає одразу; решті
    // кімнати про це скаже кадр pollClear
    if (sent) poll.value = null
    return sent
  }

  /**
   * Медіа їде звичайним HTTP: файл у сокет не запхнеш, та й не треба —
   * бекенд повертає номер файлу, і саме він летить із повідомленням
   */
  const uploadMedia = (file) =>
    uploadChatMedia(file, {
      apiBase: apiBase.value,
      token: token.value,
      maxBytes: limits.value.maxMediaBytes
    })

  if (import.meta.client) {
    // Кімнату міняють з адмінки прямо під час ефіру — тоді просто заходимо
    // в нову тим самим зʼєднанням
    watch(
      () => toValue(room),
      (next, prev) => {
        if (!next || next === prev) return
        messages.value = []
        poll.value = null
        forgetPending()
        // Саме стан сокета, а не connected: у кімнату нас могли ще не
        // пустити, але вхід у нову все одно їде цим же зʼєднанням
        if (socket?.readyState === WebSocket.OPEN) join()
        else connect()
      }
    )

    // Повернулись на вкладку після сну — зʼєднання могло тихо померти, і
    // про це ми дізнаємось тільки спробувавши
    const wake = () => {
      if (document.visibilityState === 'visible' && !connected.value) {
        retry = RECONNECT_MIN
        connect()
      }
    }

    onMounted(() => {
      closedByUs = false
      connect()
      document.addEventListener('visibilitychange', wake)
      window.addEventListener('online', wake)
    })

    onBeforeUnmount(() => {
      document.removeEventListener('visibilitychange', wake)
      window.removeEventListener('online', wake)
      disconnect()
    })
  }

  return {
    messages,
    connected,
    me,
    online,
    muted,
    mutedUntil,
    denied,
    readonly,
    notice,
    limits,
    chatters,
    rejected,
    poll,
    canModerate,
    sendMessage,
    removeMessage,
    muteUser,
    reactMessage,
    uploadMedia,
    refreshProfile,
    createPoll,
    votePoll,
    endPoll,
    dropPoll
  }
}
